import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

export interface ApproveRejectSopModalProps {
  open: boolean;
  type: 'approve' | 'reject';
  sopName: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (comments: string) => Promise<void> | void;
}

export const ApproveRejectSopModal: React.FC<ApproveRejectSopModalProps> = ({
  open,
  type,
  sopName,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [comments, setComments] = useState('');
  const isApprove = type === 'approve';

  useEffect(() => {
    if (open) {
      setComments('');
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!isApprove && !comments.trim()) return;
    await onSubmit(comments.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          overflow: 'hidden',
          maxWidth: '640px',
        },
      }}
    >
      {/* Dark blue header bar */}
      <DialogTitle
        sx={{
          bgcolor: '#0B2853',
          color: '#FFFFFF',
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.95rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          {isApprove ? 'SOP APPROVE' : 'SOP REJECT'}
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={loading}
          sx={{ color: '#FFFFFF', p: 0.5, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Light blue content area */}
      <DialogContent sx={{ bgcolor: '#F0F6FF', p: 3, pt: '24px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
          {isApprove ? (
            /* Green rounded check badge */
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '12px',
                bgcolor: '#00C48C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0, 196, 140, 0.25)',
              }}
            >
              <CheckIcon sx={{ color: '#FFFFFF', fontSize: 32, stroke: '#FFFFFF', strokeWidth: 1.5 }} />
            </Box>
          ) : (
            /* Yellow warning triangle */
            <Box
              sx={{
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#F5A623' }} />
            </Box>
          )}

          <Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 500, color: '#0F172A', lineHeight: 1.3 }}>
              {isApprove ? 'Approving the ' : 'Rejecting the '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {sopName}.
              </Box>
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#64748B', mt: 0.5 }}>
              {isApprove ? 'Add comments if you want to share any updates.' : 'Add reason for rejection.'}
            </Typography>
          </Box>
        </Box>

        {/* Input and Action button row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={isApprove ? 'Add Comments' : 'Add Comments*'}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={loading}
            inputProps={{
              style: {
                fontSize: '0.875rem',
                color: '#1E293B',
              },
            }}
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: '4px',
              '& .MuiOutlinedInput-root': {
                height: 40,
                borderRadius: '4px',
                '& fieldset': {
                  borderColor: '#CBD5E1',
                },
                '&:hover fieldset': {
                  borderColor: '#94A3B8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isApprove ? '#009BD8' : '#DC2626',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#94A3B8',
                opacity: 1,
              },
            }}
          />

          <Button
            variant="contained"
            disabled={loading || (!isApprove && !comments.trim())}
            onClick={handleSubmit}
            sx={{
              height: 40,
              minWidth: '150px',
              px: '3',
              whiteSpace: 'nowrap',
              fontWeight: 700,
              fontSize: '0.85rem',
              borderRadius: '4px',
              boxShadow: 'none',
              bgcolor: isApprove ? '#009BD8 !important' : '#DC2626 !important',
              color: '#FFFFFF !important',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: isApprove ? '#0085BA !important' : '#B91C1C !important',
                boxShadow: isApprove ? '0 2px 8px rgba(0, 155, 216, 0.3)' : '0 2px 8px rgba(220, 38, 38, 0.3)',
              },
              '&.Mui-disabled': {
                bgcolor: '#CBD5E1 !important',
                color: '#FFFFFF !important',
                opacity: 0.8,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
            ) : isApprove ? (
              'SEND APPROVAL'
            ) : (
              'REJECT'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveRejectSopModal;
